class CountryPopup {
  constructor() {
    // Set variables
    this.countryPopupCookieIdentifier = "country_popup";
    this.countryPopupData = document.querySelector("#country-popup");
    this.countryPopupModal = document.querySelector("#PopupModal-country-popup");
    this.options = JSON.parse(this.countryPopupData.dataset.countryMessageOptions);
    this.buttonTrigger = this.countryPopupData.querySelector('button');

    // Set button that should save the cookies and close the popup
    this.buttonStay = this.countryPopupModal.querySelector('[data-country-message-stay]');
    this.buttonClose = this.countryPopupModal.querySelector('.country-popup-modal__toggle');

    // Check if country popup exists
    if (!this.countryPopupData) {
      return;
    }

    // Initialize country popup
    this.init();

    // Reload popup on section load
    document.addEventListener('shopify:section:load', (e) => {
      if (e.detail.sectionId === "site-country-popup") {
        this.init();
      }
    });
  }

  /**
   * Initialize the country popup.
   * It checks the expiration of the popup, reads the cookie, and determines if the popup should be rendered.
   * It also handles the click events for the stay and close buttons.
   */
  init() {
    if (parseInt(this.options.expires_in_days) === 0) {
      document.cookie = `${this.countryPopupCookieIdentifier}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    }

    this.countryPopupCookie = this.getCookie(this.countryPopupCookieIdentifier);

    if (!this.mayRenderCountryPopup()) {
      return false;
    }

    // Get country code and render popup
    this.getLocation().then(() => {
      this.renderCountryPopup();
    });

    // Set close event for popup
    [this.buttonStay, this.buttonClose].forEach(button => {
      button.addEventListener('click', () => this.closeCountryPopup());
    });
  }

  /**
   * Checks if the country popup may be rendered.
   * It checks if the cookie is already set or if there are no popup messages available.
   * @returns {boolean} True if the popup may be rendered, false otherwise.
   */
  mayRenderCountryPopup() {
    if (this.countryPopupCookie || Object.keys(this.options.popup_messages).length === 0) {
      return false;
    }
    return true;
  }

  /**
   * Retrieves the user's location (country code) asynchronously.
   * If the country code is not stored in the cookie, it fetches it from an API and sets it in the cookie.
   * @returns {Promise<string>} A promise that resolves to the country code.
   */
  async getLocation() {
    this.country_code = this.getCookie(`${this.countryPopupCookieIdentifier}-country_code`);

    if (!this.country_code) {
      this.country_code = await this.getCountryCodeFromAPI();
      this.setCountryCode(this.country_code);
    }
    return this.country_code;
  }

  /**
   * Retrieves the country code from an API using the provided URL and field name identifier.
   * @returns {Promise<string>} A promise that resolves to the country code.
   */
  async getCountryCodeFromAPI() {
    const country_code_api = this.options.location_api_url;
    const country_code_fieldname_identifier = this.options.location_field_name || 'country_code';

    const response = await fetch(country_code_api);
    const data = await response.json();

    return data[country_code_fieldname_identifier];
  }

  /**
   * Sets the country code in a cookie.
   * @param {string} country_code - The country code to be set in the cookie.
   * @returns {string} The country code that was set.
   */
  setCountryCode(country_code) {
    if (country_code) {
      this.country_code = country_code;

      const inOneHourMinutes = new Date(new Date().getTime() + 60 * 1000);
      document.cookie = `${this.countryPopupCookieIdentifier}-country_code=${country_code}; expires=${inOneHourMinutes.toUTCString()}; path=/;`;
    }

    return country_code;
  }

  /**
   * Renders the country popup if the country code is valid and there is a message for that country.
   */
  renderCountryPopup() {
    if (!this.country_code || this.country_code === this.options.current_country_code) {
      return;
    }

    let countryData = this.options.popup_messages[this.country_code];
    if (!countryData && this.options.popup_messages['ROW']) {
      countryData = this.options.popup_messages['ROW'];
    }
    if (!countryData) {
      return;
    }

    // Set popup content
    this.countryPopupModal.querySelector('[data-country-message-title]').innerText = countryData.country_title || '';
    this.countryPopupModal.querySelector('[data-country-message-name-title]').innerText = countryData.country_name || '';
    this.countryPopupModal.querySelector('[data-country-message-description]').innerText = countryData.country_body || '';
    this.countryPopupModal.querySelector('[data-country-message-label]').innerText = countryData.country_button || '';
    if (countryData.country_url.includes('ducadelcosma.nl') && NL_URL) {
      this.countryPopupModal.querySelector('[data-country-message-url]').href = countryData.country_url + NL_URL;
    } else if (countryData.country_url.includes('de.ducadelcosma.com') && DE_URL) {
      this.countryPopupModal.querySelector('[data-country-message-url]').href = countryData.country_url + DE_URL;
    } else if (countryData.country_url.includes('ducadelcosma.co.uk') && UK_URL) {
      this.countryPopupModal.querySelector('[data-country-message-url]').href = countryData.country_url + UK_URL;
    } else if (countryData.country_url.includes('ducadelcosma.us') && USA_URL) {
      this.countryPopupModal.querySelector('[data-country-message-url]').href = countryData.country_url + USA_URL;
    } else if (countryData.country_url.includes('ducadelcosma.com') && Global_URL) {
      this.countryPopupModal.querySelector('[data-country-message-url]').href = countryData.country_url + Global_URL;
    } else {
      // Fallback: If no metafield URL is defined, use the base countryData URL or set it to '#'
      this.countryPopupModal.querySelector('[data-country-message-url]').href = countryData.country_url || '#';
    }

    this.countryPopupModal.querySelector('[data-country-message-stay]').value = countryData.country_stay || '';

    // Show popup using ModalDialog component
    if (this.countryPopupModal instanceof ModalDialog && this.buttonTrigger) {
      this.countryPopupModal.show(this.buttonTrigger);
    }
  }

  /**
   * Closes the country popup and sets the cookie to expire based on the configured expiration days.
   */
  closeCountryPopup() {
    // Hide popup using ModalDialog component
    if (this.countryPopupModal instanceof ModalDialog && this.buttonStay) {
      this.countryPopupModal.hide();
    }

    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + parseInt(this.options.expires_in_days));
    document.cookie = `${this.countryPopupCookieIdentifier}=true; expires=${expiryDate.toUTCString()}; path=/;`;
  }

  /**
   * Retrieves the value of a cookie by name.
   * @param {string} name - The name of the cookie.
   * @returns {string|null} The value of the cookie, or null if not found.
   */
  getCookie(name) {
    const cookieArr = document.cookie.split(';');
    for (let i = 0; i < cookieArr.length; i++) {
      const cookiePair = cookieArr[i].split('=');
      const cookieName = cookiePair[0].trim();
      if (cookieName === name) {
        return decodeURIComponent(cookiePair[1]);
      }
    }
    return null;
  }
}